import type { LiveBlockRundown, LivePartSegment } from './livePartPlan.js'

/**
 * Lecture de la fenêtre d'ajustement des parties d'un direct
 * (`LivePartAdjustDialog`) : état de chaque partie, frise du bloc et saisie
 * d'une durée en « mm:ss ». Module pur, sans horloge : l'instant est injecté.
 */

export type LivePartRowState = 'DONE' | 'ON_AIR' | 'RESUME' | 'UPCOMING'

/**
 * Où en est une partie par rapport à l'antenne. `RESUME` : décrochage en cours,
 * c'est la partie qui reviendra au raccrochage.
 */
export function livePartRowState(rundown: LiveBlockRundown, segment: LivePartSegment): LivePartRowState {
  const { cursor } = rundown
  if (cursor.phase === 'PART' && cursor.current?.number === segment.number) return 'ON_AIR'
  if (cursor.phase === 'BREAK' && cursor.next?.number === segment.number) return 'RESUME'
  const pivot = cursor.phase === 'PART' ? cursor.current : cursor.phase === 'BREAK' ? cursor.next : null
  if (pivot && segment.number < pivot.number) return 'DONE'
  if (cursor.phase === 'AFTER') return 'DONE'
  return 'UPCOMING'
}

export interface LiveBlockFrieze {
  startMs: number
  endMs:   number
  cells:   Array<{
    number:   number
    state:    LivePartRowState
    /** Position et largeur dans la frise, en pourcentage du bloc. */
    leftPct:  number
    widthPct: number
    /** Part déjà écoulée de la partie, 0 → 1. */
    elapsed:  number
  }>
  /** Repère de l'instant présent, `null` hors du bloc. */
  nowPct: number | null
}

/**
 * Frise proportionnelle du bloc, du début de la première partie à la fin du
 * bloc. Les coupures restent des creux, à leur échelle : c'est ce qui laisse
 * voir ce qu'une durée saisie va déplacer.
 */
export function liveBlockFrieze(rundown: LiveBlockRundown, nowMs: number): LiveBlockFrieze {
  const startMs = rundown.segments[0]?.startMs ?? nowMs
  const endMs = Math.max(startMs + 1, rundown.blockEndMs)
  const span = endMs - startMs
  const pct = (ms: number) => Math.max(0, Math.min(100, ((ms - startMs) / span) * 100))
  return {
    startMs,
    endMs,
    cells: rundown.segments.map(segment => ({
      number:   segment.number,
      state:    livePartRowState(rundown, segment),
      leftPct:  pct(segment.startMs),
      widthPct: pct(segment.endMs) - pct(segment.startMs),
      elapsed:  Math.max(0, Math.min(1, (nowMs - segment.startMs) / Math.max(1, segment.endMs - segment.startMs))),
    })),
    nowPct: nowMs >= startMs && nowMs <= endMs ? pct(nowMs) : null,
  }
}

/** « 12:00 » : minutes (sans plafond à 59) et secondes. */
export function formatMinSec(totalSec: number): string {
  const total = Math.max(0, Math.round(totalSec))
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

/**
 * Durée saisie dans le champ unique. Accepte « 12:30 », « 12:3 » (= 12:03),
 * « 12 » (minutes) et « 12:» ; `null` pour tout le reste, secondes ≥ 60
 * comprises — le champ le signale au lieu de deviner.
 */
export function parseMinSec(raw: string): number | null {
  const text = raw.trim().replace(/[.,h]/g, ':')
  const match = /^(\d{1,3})(?::(\d{0,2}))?$/.exec(text)
  if (!match) return null
  const minutes = Number(match[1])
  const seconds = match[2] ? Number(match[2]) : 0
  if (seconds >= 60) return null
  return minutes * 60 + seconds
}
